import { useState, useEffect } from 'react';
import type { FC } from 'react';
import type { Truck } from '../types';
import DeviceStatusCard from './DeviceStatusCard';
import { useDevices } from '../contexts/DeviceContext';
import './Sidebar.css';

const formatDuration = (seconds: number): string => {
  if (seconds < 0) return '0s';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) return `${hours}h ${minutes}min`;
  if (minutes > 0) return `${minutes}min ${secs}s`;
  return `${secs}s`;
};

interface SidebarProps {
  trucks: Truck[];
  selectedTruck: Truck | null;
  onTruckSelect: (truck: Truck | null) => void;
  isOpen: boolean;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  loading: boolean;
  error: string | null;
}

type SidebarView = 'fleet' | 'device-detail';

const Sidebar: FC<SidebarProps> = ({
  trucks,
  selectedTruck,
  onTruckSelect,
  isOpen,
  isExpanded = true,
  onExpandToggle,
  loading,
  error,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'moving' | 'idle'>('all');
  const [view, setView] = useState<SidebarView>('fleet');
  const [currentTime, setCurrentTime] = useState(new Date());

  const { devices, selectedDevice, setSelectedDevice, onlineCount, lastUpdate } = useDevices();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getTimeSinceStatusChange = (statusChangedAt: string | undefined, lastSeen: string): number => {
    const timestamp = statusChangedAt || lastSeen;
    return (currentTime.getTime() - new Date(timestamp).getTime()) / 1000;
  };

  const filteredTrucks = trucks.filter((truck) => {
    const matchesSearch =
      truck.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      truck.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      truck.plateNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || truck.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusText = (status: string) => {
    switch (status) {
      case 'moving': return 'I rörelse';
      case 'idle': return 'Stillastående';
      default: return 'Offline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'moving': return 'var(--color-success)';
      case 'idle': return 'var(--color-warning)';
      default: return 'var(--color-danger)';
    }
  };

  const formatSpeed = (speed: number) => `${Math.round(speed)} km/h`;

  const formatTime = (dateString: string) => {
    const diffMins = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
    if (diffMins < 1) return 'Just nu';
    if (diffMins < 60) return `${diffMins} min sedan`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} tim sedan`;
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  const openDeviceDetail = (device: typeof devices[0]) => {
    setSelectedDevice(device);
    setView('device-detail');
  };

  const backToFleet = () => {
    setView('fleet');
    setSelectedDevice(null);
  };

  // ------ Collapsed state: floating toggle ------
  if (!isOpen || !isExpanded) {
    return (
      <div className="sidebar-toggle-wrapper">
        <button
          className="sidebar-open-btn"
          onClick={onExpandToggle}
          title="Öppna sidopanel"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
      </div>
    );
  }

  // ------ Device detail view ------
  if (view === 'device-detail' && selectedDevice) {
    return (
      <div className="sidebar-wrapper open has-expanded">
        <aside className="sidebar expanded open">
          <div className="sidebar-header">
            <button className="back-btn" onClick={backToFleet}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <h2 className="sidebar-page-title">Enhetsdetalj</h2>
            <button className="sidebar-collapse-btn" onClick={onExpandToggle} title="Stäng panel">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="sidebar-device-status">
            <DeviceStatusCard
              deviceId={selectedDevice.deviceId}
              onClose={backToFleet}
            />
          </div>
        </aside>
      </div>
    );
  }

  // ------ Fleet / main view ------
  return (
    <div className="sidebar-wrapper open has-expanded">
      <aside className="sidebar expanded open">
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-header-main">
            <h2 className="sidebar-page-title">Fordonsflotta</h2>
            <span className="truck-count">{trucks.length} fordon</span>
          </div>
          <button className="sidebar-collapse-btn" onClick={onExpandToggle} title="Stäng panel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="sidebar-search">
          <div className="search-input-wrapper">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Sök fordon eller enhet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Filters (only when trucks exist) */}
        {trucks.length > 0 && (
          <div className="sidebar-section">
            <div className="sidebar-filters">
              <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
                Alla
              </button>
              <button className={`filter-btn ${filter === 'moving' ? 'active' : ''}`} onClick={() => setFilter('moving')}>
                <span className="status-dot moving" /> I rörelse
              </button>
              <button className={`filter-btn ${filter === 'idle' ? 'active' : ''}`} onClick={() => setFilter('idle')}>
                <span className="status-dot idle" /> Stilla
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="sidebar-content">
          {loading ? (
            <div className="sidebar-loading">
              <div className="loading-spinner" />
              <p>Hämtar data...</p>
            </div>
          ) : error ? (
            <div className="sidebar-error">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              <p>{error}</p>
              <button className="btn btn-secondary" onClick={() => window.location.reload()}>Försök igen</button>
            </div>
          ) : (
            <>
              {/* Truck list */}
              {filteredTrucks.length > 0 && (
                <ul className="truck-list">
                  {filteredTrucks.map((truck, index) => (
                    <li
                      key={truck.id}
                      className={`truck-item status-${truck.status} ${selectedTruck?.id === truck.id ? 'selected' : ''}`}
                      onClick={() => onTruckSelect(truck)}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="truck-item-header">
                        <div className="truck-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="1" y="3" width="15" height="13" rx="2" />
                            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                            <circle cx="5.5" cy="18.5" r="2.5" />
                            <circle cx="18.5" cy="18.5" r="2.5" />
                          </svg>
                        </div>
                        <div className="truck-info">
                          <h4 className="truck-name">{truck.name}</h4>
                          {truck.plateNumber && <span className="truck-plate">{truck.plateNumber}</span>}
                        </div>
                        <div className="truck-status" style={{ '--status-color': getStatusColor(truck.status) } as React.CSSProperties}>
                          <span className={`status-dot ${truck.status}`} />
                        </div>
                      </div>
                      <div className="truck-item-details">
                        <div className="detail">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                          </svg>
                          <span>{formatSpeed(truck.speed)}</span>
                        </div>
                        <div className="detail">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          <span>{formatTime(truck.lastUpdate)}</span>
                        </div>
                      </div>
                      <div className="truck-status-text">{getStatusText(truck.status)}</div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Device list */}
              {devices.length > 0 && (
                <div className="sidebar-devices">
                  {filteredTrucks.length > 0 && <div className="section-divider" />}
                  <div className={`devices-header ${onlineCount > 0 ? 'has-online' : ''}`}>
                    <h3>Enheter</h3>
                    <div className="devices-header-right">
                      <span className="live-indicator">
                        <span className="live-dot" />
                        Live
                      </span>
                      <span className="device-count">{onlineCount > 0 ? `${onlineCount} online` : 'Inga online'}</span>
                    </div>
                  </div>
                  {lastUpdate && (
                    <p className="devices-hint">
                      Uppdaterad {new Date(lastUpdate).toLocaleTimeString('sv-SE')}
                    </p>
                  )}

                  <ul className="device-list">
                    {devices.map((device) => (
                      <li
                        key={device.deviceId}
                        className={`device-item ${device.isOnline ? 'online' : 'offline'} ${selectedDevice?.deviceId === device.deviceId ? 'selected' : ''}`}
                        onClick={() => openDeviceDetail(device)}
                      >
                        <div className="device-item-header">
                          <div className="device-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="2" y="3" width="20" height="14" rx="2" />
                              <path d="M8 21h8" />
                              <path d="M12 17v4" />
                            </svg>
                            <span className={`online-indicator ${device.isOnline ? 'online' : 'offline'}`} />
                          </div>
                          <div className="device-info">
                            <h4 className="device-name">{device.deviceName}</h4>
                            <span className="device-model">{device.model}</span>
                          </div>
                          <div className="device-item-stats device-item-stats-top">
                            <div className="stat">
                              <span className="label">CPU</span>
                              <span className="value">{device.isOnline && device.cpuUsage != null ? `${device.cpuUsage.toFixed(0)}%` : '-'}</span>
                            </div>
                            <div className="stat">
                              <span className="label">Temp</span>
                              <span className="value">{device.isOnline && device.temperature != null ? `${device.temperature}°C` : '-'}</span>
                            </div>
                            <div className="stat">
                              <span className="label">Signal</span>
                              <div className={`signal-mini ${!device.isOnline ? 'offline' : ''}`}>
                                {[1, 2, 3, 4, 5].map((bar) => (
                                  <div key={bar} className={`bar ${device.isOnline && (device.signalBars ?? 0) >= bar ? 'active' : ''}`} />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="device-status-text">
                          <span className="device-status-label">
                            {device.isOnline ? 'Online' : 'Offline'}
                            {device.isOnline && device.connectionType ? ` · ${device.connectionType}` : ''}
                          </span>
                          <span className="device-status-time">
                            {formatDuration(getTimeSinceStatusChange(device.statusChangedAt, device.lastSeen))}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Empty state */}
              {filteredTrucks.length === 0 && devices.length === 0 && (
                <div className="sidebar-empty">
                  <div className="empty-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="2" y="3" width="20" height="14" rx="2" />
                      <path d="M8 21h8" />
                      <path d="M12 17v4" />
                    </svg>
                  </div>
                  <h3>Inga enheter eller fordon</h3>
                  <p>Väntar på data från RUT956...</p>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;
